import type { ChipFontKey } from "../../overlay/chip.js";
import {
  CHIP_STYLE_CHOICES,
  LAYOUT_CHOICES,
  THEME_COLORS,
  type ChipStyle,
  type Layout,
  type ThemeKey,
  type Variant,
} from "../../overlay/constants.generated.js";
import { POSITION_CHOICES, type Position } from "../../overlay/positions.js";
import type { LogoOptions, HashtagOptions } from "../../state.js";

interface OptionsPanelProps {
  logo: LogoOptions;
  hashtag: HashtagOptions;
  disabled: boolean;
  onLogoChange: (next: LogoOptions) => void;
  onHashtagChange: (next: HashtagOptions) => void;
}

const POS_LABELS: Record<Position, string> = {
  "top-left": "↖ 左上",
  "top-right": "↗ 右上",
  "bottom-left": "↙ 左下",
  "bottom-right": "↘ 右下",
};

const VARIANT_LABELS: Record<Variant | "auto", string> = {
  auto: "Auto(依背景自動挑)",
  color: "標準色 (color)",
  black: "反黑 (black)",
  inverted: "反白 (inverted)",
  kanji_inverted: "標準字反白 (kanji_inverted)",
  dark_standard: "深色底標準色 (dark_standard)",
};

const FONT_LABELS: Record<ChipFontKey, string> = {
  "microsoft-jhenghei": "微軟正黑體(系統)",
  "gensen-rounded": "源泉圓體(W7 Bold)",
};

const THEME_LABELS: Record<ThemeKey, string> = {
  future_life: "#未來生活解密 (紫)",
  tech_story: "#解密科技故事 (綠)",
  history_today: "#歷史上的今天 (紅)",
  tech_charge: "#科技快充站 (橙)",
  tech_everyday: "#解密科技Everyday (藍)",
  tech_news: "#科技報報 (青)",
};

const THEME_BUTTON_TEXT: Record<ThemeKey, string> = {
  future_life: "#未來生活解密",
  tech_story: "#解密科技故事",
  history_today: "#歷史上的今天",
  tech_charge: "#科技快充站",
  tech_everyday: "#解密科技Everyday",
  tech_news: "#科技報報(機動)",
};

const VARIANT_KEYS: (Variant | "auto")[] = [
  "auto",
  ...Object.keys(VARIANT_LABELS).filter((k) => k !== "auto") as Variant[],
];

export function OptionsPanel({
  logo,
  hashtag,
  disabled,
  onLogoChange,
  onHashtagChange,
}: OptionsPanelProps) {
  return (
    <>
      <h2>2. 設定 Logo 與 Hashtag</h2>
      <div className="options-grid">
        <div className="options-block">
          <LogoOptionsBlock options={logo} disabled={disabled} onChange={onLogoChange} />
        </div>
        <div className="options-block">
          <HashtagOptionsBlock options={hashtag} disabled={disabled} onChange={onHashtagChange} />
        </div>
      </div>
    </>
  );
}

function LogoOptionsBlock({
  options: o,
  disabled,
  onChange,
}: {
  options: LogoOptions;
  disabled: boolean;
  onChange: (next: LogoOptions) => void;
}) {
  const previewVariant = o.variant === "auto" ? "color" : o.variant;
  const previewSrc = `/logos/${o.layout}_${previewVariant}.png`;
  const previewLabel =
    o.variant === "auto"
      ? "預覽:color 變體(實際依底圖明度自動挑)"
      : `預覽:${o.layout} / ${o.variant}`;

  const togglePosition = (pos: Position, checked: boolean) => {
    let next: Position[];
    if (checked) {
      next = [...o.positions, pos];
    } else {
      next = o.positions.filter((p) => p !== pos);
      if (next.length === 0) return;
    }
    onChange({ ...o, positions: next });
  };

  return (
    <>
      <h3>DOIT Logo</h3>
      <div className="field">
        <label className="field-label">位置(可複選)</label>
        <div className="position-grid">
          {POSITION_CHOICES.map((p) => (
            <label key={p} htmlFor={`logo-pos-${p}`}>
              <input
                type="checkbox"
                id={`logo-pos-${p}`}
                checked={o.positions.includes(p)}
                disabled={disabled}
                onChange={(e) => togglePosition(p, e.target.checked)}
              />
              {POS_LABELS[p]}
            </label>
          ))}
        </div>
        <span
          className="position-quick"
          onClick={() => onChange({ ...o, positions: [...POSITION_CHOICES] })}
        >
          全部
        </span>
      </div>
      <div className="field">
        <label className="field-label">變體</label>
        <select
          value={o.variant}
          disabled={disabled}
          onChange={(e) => onChange({ ...o, variant: e.target.value as Variant | "auto" })}
        >
          {VARIANT_KEYS.map((v) => (
            <option key={v} value={v}>{VARIANT_LABELS[v]}</option>
          ))}
        </select>
      </div>
      <div className="field">
        <label className="field-label">版面</label>
        <select
          value={o.layout}
          disabled={disabled}
          onChange={(e) => onChange({ ...o, layout: e.target.value as Layout })}
        >
          {LAYOUT_CHOICES.map((l) => (
            <option key={l} value={l}>{l}</option>
          ))}
        </select>
      </div>
      <div className="logo-preview">
        <div className="logo-preview-label">{previewLabel}</div>
        <div className="logo-preview-frame">
          <img src={previewSrc} alt={previewLabel} />
        </div>
      </div>
    </>
  );
}

function HashtagOptionsBlock({
  options: o,
  disabled,
  onChange,
}: {
  options: HashtagOptions;
  disabled: boolean;
  onChange: (next: HashtagOptions) => void;
}) {
  const fillIsCustom = o.fillMode === "custom";
  const effectiveFill = fillIsCustom ? o.customColor : THEME_COLORS[o.theme]!;
  const previewFontFamily =
    o.fontKey === "gensen-rounded"
      ? `"GenSenRounded W7", "Microsoft JhengHei", "Noto Sans TC", sans-serif`
      : `"Microsoft JhengHei", "PingFang TC", "Noto Sans TC", sans-serif`;

  const togglePosition = (pos: Position, checked: boolean) => {
    let next: Position[];
    if (checked) {
      next = [...o.positions, pos];
    } else {
      next = o.positions.filter((p) => p !== pos);
      if (next.length === 0) return;
    }
    onChange({ ...o, positions: next });
  };

  return (
    <>
      <h3>Hashtag Chip</h3>

      <div className="field">
        <label className="field-label">
          主題快選 {o.fillMode === "theme" ? `(已選:${o.theme})` : "(目前自訂模式)"}
        </label>
        <div className="theme-chip-row" style={{ fontFamily: previewFontFamily }}>
          {(Object.keys(THEME_LABELS) as ThemeKey[]).map((k) => (
            <button
              key={k}
              type="button"
              className={`theme-chip-btn ${o.theme === k && !fillIsCustom ? "active" : ""}`}
              style={{ background: THEME_COLORS[k] }}
              disabled={disabled}
              onClick={() =>
                onChange({
                  ...o,
                  fillMode: "theme",
                  theme: k,
                  text: THEME_BUTTON_TEXT[k]!,
                  customColor: THEME_COLORS[k]!,
                })
              }
            >
              {THEME_BUTTON_TEXT[k]}
            </button>
          ))}
        </div>
        <div className="theme-chip-hint">按一下即套用文字 + 填色,並切回主題模式。</div>
      </div>

      <div className="field">
        <label className="field-label">文字</label>
        <input
          type="text"
          value={o.text}
          placeholder="#未來生活解密"
          disabled={disabled}
          onChange={(e) => onChange({ ...o, text: e.target.value })}
        />
      </div>

      <div className="field">
        <label className="field-label">位置(可複選)</label>
        <div className="position-grid">
          {POSITION_CHOICES.map((p) => (
            <label key={p} htmlFor={`tag-pos-${p}`}>
              <input
                type="checkbox"
                id={`tag-pos-${p}`}
                checked={o.positions.includes(p)}
                disabled={disabled}
                onChange={(e) => togglePosition(p, e.target.checked)}
              />
              {POS_LABELS[p]}
            </label>
          ))}
        </div>
        <span
          className="position-quick"
          onClick={() => onChange({ ...o, positions: [...POSITION_CHOICES] })}
        >
          全部
        </span>
      </div>

      <div className="field">
        <label className="field-label">字型</label>
        <select
          value={o.fontKey}
          disabled={disabled}
          onChange={(e) => onChange({ ...o, fontKey: e.target.value as ChipFontKey })}
        >
          {(Object.keys(FONT_LABELS) as ChipFontKey[]).map((k) => (
            <option key={k} value={k}>{FONT_LABELS[k]}</option>
          ))}
        </select>
      </div>

      <div className="field">
        <label className="field-label">自訂填色(覆蓋主題)</label>
        <div className="color-row">
          <input
            type="color"
            value={effectiveFill}
            disabled={disabled}
            onChange={(e) =>
              onChange({ ...o, fillMode: "custom", customColor: e.target.value.toUpperCase() })
            }
          />
          <span className="hex-display">{effectiveFill ?? o.customColor}</span>
          <span className="custom-mode-hint" style={{ marginLeft: "0.5rem", fontSize: "0.75rem", color: "var(--fg-muted)" }}>
            {fillIsCustom ? "自訂模式" : "主題模式 — 改顏色將切到自訂"}
          </span>
        </div>
      </div>

      <div className="field">
        <label className="field-label">文字顏色</label>
        <div className="color-row">
          <input
            type="color"
            value={o.textColor}
            disabled={disabled}
            onChange={(e) => onChange({ ...o, textColor: e.target.value.toUpperCase() })}
          />
          <span className="hex-display">{o.textColor}</span>
        </div>
      </div>

      <div className="field">
        <label className="field-label">Chip 風格</label>
        <select
          value={o.chipStyle}
          disabled={disabled}
          onChange={(e) => onChange({ ...o, chipStyle: e.target.value as ChipStyle })}
        >
          {CHIP_STYLE_CHOICES.map((s) => (
            <option key={s} value={s}>
              {s} {s === "tight" ? "(對齊 ref)" : "(預設)"}
            </option>
          ))}
        </select>
      </div>
    </>
  );
}
